import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import { styled } from '@mui/material/styles';
import { APIService } from 'src/api/api';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import ScreenHeader from 'src/common/ScreenHeader';
import useAuth from 'src/context/AuthContext';
import getStaticAssetUrl from 'src/utils/getStaticAssetUrl';
import { USER_ROLES, USER_TYPES } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import { loginValidationSchema } from 'src/utils/getInitials';

const BRAND_COLORS = {
  white: '#ffffff',
  navy: '#102036',
  blue: '#3682ae',
  deepBlue: '#004a80'
};
const FIELD_ICON_RAIL_WIDTH = 36;
const FIELD_TEXT_INSET = 58;
const AUTH_FIELD_HEIGHT = 48;
const FIELD_SEPARATOR_COLOR = '#b9d4e5';
const FIELD_SEPARATOR_HEIGHT = 28;
const DENSE_FIELD_RAIL_OFFSET = 2;
// All 4 product images are 1024×1536 (2:3 portrait ratio).
// Card size scales responsively with BOTH viewport width AND height on desktop,
// so that in normal browser window mode (and smaller screens/laptops),
// the content never pushes the top heading off-screen.
const PRODUCT_CARD_WIDTH = 'var(--product-card-width, clamp(220px, min(27vw, 38vh), 480px))';

// Replace each image value with a local asset path when product screenshots are ready.
const products = [
  {
    name: 'PRUDENT', tagline: 'Fuel Management', image: '/static/images/products/prudent.png', accent: '#58a9d4', mark: 'P'
  },
  {
    name: 'PRULINK', tagline: 'Asset & Vehicle Tracking', image: '/static/images/products/prulink.png', accent: '#72c3bd', mark: 'L'
  },
  {
    name: 'PRISM', tagline: 'Data Analytics & Reporting', image: '/static/images/products/prism.png', accent: '#8b9cf1', mark: 'I'
  },
  {
    name: 'FLEET MANAGEMENT', tagline: 'Fleet Monitoring', image: '/static/images/products/fleet-management.png', accent: '#e2ad68', mark: 'F'
  }
];
const carouselPositions = [0, 1, -2, -1];
// depth-2 card is fully hidden (opacity 0) so only the symmetric left/center/right
// triad is ever visible, keeping the active card exactly at the container midpoint.
const sideOpacity = { 1: 0.65, 2: 0 };
const sideScale = { 1: 0.84, 2: 0.68 };
const sideLayer = { 1: 2, 2: 1 };

const SOCIAL_LINKS = [
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@retrack-automation',
    Icon: YouTubeIcon
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/retrack-automation/',
    Icon: LinkedInIcon
  },
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/retrackautomation',
    Icon: FacebookIcon
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/retrackautomation/',
    Icon: InstagramIcon
  },
  {
    name: 'Twitter',
    url: 'https://twitter.com/retrackauto',
    Icon: TwitterIcon
  }
];

export const LayoutWrapper = styled('div')({
  '--product-card-width': 'clamp(210px, 46vw, 290px)',
  '@media (min-width: 1200px)': {
    '--product-card-width': 'clamp(220px, min(27vw, 38vh), 480px)'
  },
  alignItems: 'center',
  backgroundColor: BRAND_COLORS.navy,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  minHeight: '100vh',
  overflowX: 'hidden',
  '@supports (height: 100dvh)': {
    height: '100dvh'
  },
  '@media (max-width: 1199.95px)': {
    height: 'auto',
    minHeight: '100vh'
  },
  '@media (min-width: 1200px) and (min-height: 560px)': {
    overflowY: 'hidden'
  },
  '@media (min-width: 1200px) and (max-height: 559.95px)': {
    overflowY: 'auto',
    height: 'auto'
  }
});

const authFieldSx = {
  '& .MuiInputLabel-root': {
    color: BRAND_COLORS.deepBlue,
    fontSize: 14
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: BRAND_COLORS.white,
    borderRadius: '6px',
    boxSizing: 'border-box',
    height: AUTH_FIELD_HEIGHT,
    minHeight: AUTH_FIELD_HEIGHT,
    padding: 0,
    '& fieldset': {
      borderColor: '#b9d4e5',
      borderWidth: '1px'
    },
    '&:hover fieldset': {
      borderColor: BRAND_COLORS.blue
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_COLORS.deepBlue,
      borderWidth: 1
    },
    '&.Mui-error fieldset': {
      borderColor: '#da1e28'
    }
  },
  '& .MuiOutlinedInput-input': {
    appearance: 'none',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    color: BRAND_COLORS.navy,
    fontSize: 15,
    height: '100%',
    lineHeight: 1.5,
    minWidth: 0,
    outline: 'none',
    padding: `0 14px 0 ${FIELD_TEXT_INSET}px !important`
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0
  }
};

const authSelectSx = {
  ...authFieldSx,
  '& .MuiSelect-select': {
    alignItems: 'center',
    appearance: 'none',
    boxSizing: 'border-box',
    display: 'flex',
    fontSize: 15,
    height: '100% !important',
    lineHeight: 1.5,
    minHeight: '0 !important',
    outline: 'none',
    padding: `0 38px 0 ${FIELD_TEXT_INSET}px !important`,
    WebkitAppearance: 'none',
    '&:focus': {
      backgroundColor: `${BRAND_COLORS.white} !important`,
      borderRadius: '6px'
    }
  }
};

const renderFieldStartAdornment = (Icon, isDenseField = false) => (
  <Box
    aria-hidden="true"
    sx={{
      alignItems: 'center',
      color: BRAND_COLORS.blue,
      display: 'flex',
      justifyContent: 'space-between',
      left: 14,
      pointerEvents: 'none',
      position: 'absolute',
      top: isDenseField
        ? `calc(50% + ${DENSE_FIELD_RAIL_OFFSET}px)`
        : '50%',
      transform: 'translateY(-50%)',
      width: `${FIELD_ICON_RAIL_WIDTH}px`,
      zIndex: 2
    }}
  >
    <Icon sx={{ fontSize: 21 }} />
    <Box
      sx={{
        backgroundColor: FIELD_SEPARATOR_COLOR,
        flex: '0 0 1px',
        height: FIELD_SEPARATOR_HEIGHT,
        opacity: 0.85,
        width: 1
      }}
    />
  </Box>
);

const Login = () => {
  const history = useHistory();
  const auth = useAuth();
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeProduct, setActiveProduct] = useState(0);
  const [carouselTimerReset, setCarouselTimerReset] = useState(0);
  const initValues = { email: '', password: '', role: 'admin' };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setActiveProduct((current) => (current + 1) % products.length);
    }, 3000);
    return () => window.clearTimeout(timeout);
  }, [activeProduct, carouselTimerReset]);

  const getLoggedInUserRole = (user, values) => {
    let loggedInUserRole = user.role || values.role;
    if (user.role === USER_ROLES.admin) {
      if (values.role !== USER_ROLES.admin) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.admin;
    } else if (user.role === USER_ROLES.supervisor) {
      if (values.role !== USER_ROLES.requestor) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.requestor;
    } else if (user.role === USER_ROLES.operator) {
      if (values.role !== USER_ROLES.operator) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.operator;
    } else if (user.role === USER_ROLES.support) {
      if (values.role !== USER_ROLES.support) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.support;
    }
    return loggedInUserRole;
  };

  const getPostLoginRoute = (role) => {
    if (role === USER_ROLES.operator) return ROUTE.cardApprovals;
    if (role === USER_ROLES.support) return ROUTE.support;
    return ROUTE.app;
  };

  const login = async (values) => {
    setLoginError('');
    try {
      const res = await APIService.userLogin(values);
      if (!res.success) throw res;
      if (res.result.token) {
        const userRole = getLoggedInUserRole(res.result, values);
        auth.signin({ ...res.result, role: userRole });
        history.replace(getPostLoginRoute(userRole));
      }
    } catch (error) {
      setLoginError('Invalid credentials, Please try again!');
      console.log('Login error', error.message);
    }
  };

  return (
    <LayoutWrapper>
      <ScreenHeader title="Login" />
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: { xs: 'center', lg: 'space-between' },
          alignItems: 'center',
          gap: { xs: 4, md: 5, lg: 'clamp(24px, 3.5vw, 56px)' },
          flex: '1 1 auto',
          maxWidth: { xs: '100%', xl: '1720px' },
          mx: 'auto',
          minHeight: 0,
          overflow: { xs: 'visible', lg: 'hidden' },
          px: {
            xs: 2, sm: 4, lg: 7, xl: 9
          },
          py: { xs: 2.5, lg: 'clamp(10px, 1.8vh, 28px)' },
          width: '100%'
        }}
      >
        <Box
          sx={{
            color: BRAND_COLORS.white,
            display: 'flex',
            alignSelf: 'center',
            flex: '1 1 0',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: { xs: '100%', lg: 'min(60vw, 980px)', xl: 'min(60vw, 1100px)' },
            minWidth: 0,
            width: '100%'
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: 26, sm: 28, lg: 'clamp(22px, 2.4vh, 32px)', xl: 'clamp(24px, 2.8vh, 32px)'
              },
              fontWeight: 650,
              letterSpacing: 0,
              lineHeight: 1.15,
              mb: { xs: 1.5, lg: 'clamp(8px, 1.6vh, 20px)' },
              maxWidth: 590
            }}
          >
            Smarter Fuel Operations
            <br />
            in
            {' '}
            <Box component="span" sx={{ color: '#079fe4' }}>Every Direction.</Box>
          </Typography>

          <Box
            aria-label="Product showcase"
            sx={{
              // Stage height = card width × 1.5 (2:3 image ratio) so it always
              // matches the card height exactly without a separate constant.
              height: `calc(${PRODUCT_CARD_WIDTH} * 1.5)`,
              flex: `0 0 calc(${PRODUCT_CARD_WIDTH} * 1.5)`,
              overflow: 'hidden',
              perspective: '1100px',
              position: 'relative',
              mt: { xs: 3, lg: 'clamp(40px, 6vh, 80px)' },
              width: '100%'
            }}
          >
            {products.map((product, index) => {
              const position = (index - activeProduct + products.length) % products.length;
              const offset = carouselPositions[position];
              const isActive = position === 0;
              const depth = Math.abs(offset);
              const opacity = isActive ? 1 : sideOpacity[depth];
              const scale = isActive ? 1 : sideScale[depth];
              return (
                <Box
                  key={product.name}
                  aria-hidden={!isActive}
                  aria-label={product.name}
                  sx={{
                    backfaceVisibility: 'hidden',
                    background: `radial-gradient(ellipse at 50% 4%, ${product.accent}28, transparent 46%), linear-gradient(155deg, rgba(16,69,112,0.78), rgba(10,38,66,0.88) 48%, rgba(6,24,43,0.94)), #10263d`,
                    border: `1px solid ${isActive ? `${product.accent}88` : 'rgba(255,255,255,0.16)'}`,
                    borderRadius: '18px',
                    boxShadow: isActive
                      ? `0 22px 52px rgba(0,8,20,0.45), 0 0 30px ${product.accent}20, inset 0 1px 0 rgba(255,255,255,0.14)`
                      : '0 14px 32px rgba(0,8,20,0.32)',
                    // aspectRatio matches images exactly (1024×1536 = 2:3)
                    // so the image fills edge-to-edge with zero cropping or gaps.
                    aspectRatio: '2/3',
                    left: '50%',
                    opacity,
                    overflow: 'hidden',
                    p: 0,
                    pointerEvents: opacity === 0 ? 'none' : 'auto',
                    position: 'absolute',
                    top: '50%',
                    transform: `translate(calc(-50% + ${offset} * calc(${PRODUCT_CARD_WIDTH} * 0.72)), -50%) scale(${scale}) rotateY(${offset * -8}deg)`,
                    transformOrigin: 'center center',
                    transition: 'transform 720ms cubic-bezier(0.22, 0.75, 0.25, 1), opacity 720ms ease-out, box-shadow 720ms ease-out',
                    width: PRODUCT_CARD_WIDTH,
                    zIndex: isActive ? 3 : sideLayer[depth],
                  }}
                >
                  <Box
                    component="img"
                    src={getStaticAssetUrl(product.image)}
                    alt={`${product.name} product`}
                    sx={{
                      display: 'block',
                      height: '100%',
                      opacity: 0.94,
                      objectFit: 'cover',
                      objectPosition: 'center center',
                      width: '100%'
                    }}
                  />
                </Box>
              );
            })}
          </Box>
          <Box
            aria-label="Product showcase position"
            sx={{
              display: 'flex', gap: 1, justifyContent: 'center', mt: { xs: 1.5, lg: 'clamp(8px, 1.6vh, 20px)' }
            }}
          >
            {products.map((product, index) => (
              <ButtonBase
                key={product.name}
                aria-label={`${product.name}${index === activeProduct ? ', current product' : ''}`}
                aria-pressed={index === activeProduct}
                disableRipple
                onClick={() => {
                  setActiveProduct(index);
                  setCarouselTimerReset((current) => current + 1);
                }}
                type="button"
                sx={{
                  alignItems: 'center',
                  borderRadius: '50%',
                  display: 'flex',
                  height: 16,
                  justifyContent: 'center',
                  minWidth: 16,
                  p: 0,
                  '&:focus-visible': {
                    outline: '2px solid #55a9d7',
                    outlineOffset: 2
                  }
                }}
              >
                <Box
                  component="span"
                  sx={{
                    backgroundColor: index === activeProduct ? '#55a9d7' : 'rgba(255,255,255,0.32)',
                    borderRadius: '50%',
                    display: 'block',
                    height: 8,
                    transition: 'background-color 300ms ease, transform 300ms ease',
                    transform: index === activeProduct ? 'scale(1.15)' : 'scale(1)',
                    width: 8
                  }}
                />
              </ButtonBase>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid rgba(255, 255, 255, 0.72)',
            borderRadius: '10px',
            boxShadow: '0 30px 80px rgba(16, 32, 54, 0.32)',
            flex: '0 1 430px',
            maxWidth: 430,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            zIndex: 1
          }}
        >
          <Box
            sx={{
              px: { xs: 3, sm: 4 },
              pt: { xs: 2.5, sm: 'clamp(16px, 2.5vh, 28px)' },
              pb: { xs: 1.5, sm: 'clamp(10px, 1.6vh, 20px)' }
            }}
          >
            <Box
              component="img"
              src={getStaticAssetUrl('/static/images/retrack-logo.png')}
              alt="Retrack Automation"
              sx={{
                display: 'block',
                height: { xs: 38, sm: 'clamp(36px, 4.2vh, 46px)' },
                maxWidth: '100%',
                objectFit: 'contain',
                objectPosition: 'left center',
                width: { xs: 180, sm: 'auto' }
              }}
            />
            <Typography
              sx={{
                color: BRAND_COLORS.navy,
                fontSize: { xs: 20, sm: 'clamp(20px, 2.4vh, 24px)' },
                fontWeight: 700,
                letterSpacing: 0,
                lineHeight: 1.2,
                mt: { xs: 2, sm: 'clamp(12px, 1.8vh, 22px)' }
              }}
            >
              Sign in to FMS
            </Typography>
            <Typography
              sx={{
                color: BRAND_COLORS.deepBlue,
                fontSize: { xs: 13, sm: 'clamp(12.5px, 1.4vh, 14px)' },
                letterSpacing: 0,
                lineHeight: 1.4,
                mt: 0.5
              }}
            >
              Use your assigned role to access the dashboard.
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 2.5, sm: 'clamp(16px, 2.5vh, 28px)' } }}>
            <Formik
              initialValues={initValues}
              validationSchema={loginValidationSchema}
              onSubmit={login}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ position: 'relative' }}>
                    {renderFieldStartAdornment(MailOutlineIcon, true)}
                    <InputField
                      name="email"
                      label="Email"
                      type="email"
                      margin="dense"
                      fullWidth
                      disabled={isSubmitting}
                      autoComplete="email"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      sx={authFieldSx}
                    />
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    {renderFieldStartAdornment(LockOutlinedIcon, true)}
                    <InputField
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      margin="dense"
                      fullWidth
                      disabled={isSubmitting}
                      autoComplete="current-password"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      sx={authFieldSx}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              aria-pressed={showPassword}
                              size="small"
                              sx={{ mr: 0.5 }}
                              onClick={() => setShowPassword((isVisible) => !isVisible)}
                              disabled={isSubmitting}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  <Box sx={{ mt: 2, position: 'relative' }}>
                    {renderFieldStartAdornment(PersonOutlineIcon)}
                    <SelectField
                      name="role"
                      label="Select Role"
                      value={initValues.role}
                      data={USER_TYPES}
                      fullWidth
                      disabled={isSubmitting}
                      sx={authSelectSx}
                    />
                  </Box>
                  <FormHelperText
                    error
                    sx={{
                      minHeight: 24,
                      ml: 0,
                      mt: 1.25
                    }}
                  >
                    {loginError}
                  </FormHelperText>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    endIcon={isSubmitting ? null : <LoginRoundedIcon />}
                    sx={{
                      backgroundColor: BRAND_COLORS.deepBlue,
                      borderRadius: '6px',
                      boxShadow: 'none',
                      fontSize: 15,
                      fontWeight: 700,
                      justifyContent: 'space-between',
                      letterSpacing: 0,
                      mt: 0.5,
                      minHeight: 48,
                      px: 2.25,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: BRAND_COLORS.navy,
                        boxShadow: 'none'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#c4d9e7',
                        color: BRAND_COLORS.deepBlue
                      },
                      '& .MuiButton-endIcon': {
                        ml: 'auto'
                      }
                    }}
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      'Continue to dashboard'
                    )}
                  </Button>
                </form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{
          alignItems: 'center',
          borderTop: '1px solid rgba(172,208,232,0.12)',
          boxSizing: 'border-box',
          columnGap: { xs: 2, md: 3 },
          color: '#b9cfe0',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
          px: {
            xs: 2, sm: 4, lg: 7, xl: 9
          },
          py: { xs: 2, md: 'clamp(6px, 1vh, 12px)' },
          rowGap: { xs: 2, md: 0.75 },
          width: '100%',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            alignItems: { xs: 'center', md: 'flex-start' },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            justifyContent: { xs: 'center', md: 'flex-start' }
          }}
        >
          <Typography
            sx={{
              color: '#c7d9e7',
              fontSize: 10,
              letterSpacing: '0.24em',
              lineHeight: 1.8,
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            FUELING EFFICIENCY.
            <br />
            DRIVING A SMARTER TOMORROW.
          </Typography>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1.25,
              justifyContent: { xs: 'center', md: 'flex-start' }
            }}
          >
            {SOCIAL_LINKS.map(({ name, url, Icon }) => (
              <IconButton
                key={name}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  color: '#ffffff',
                  height: 32,
                  width: 32,
                  p: 0,
                  transition: 'background-color 200ms ease, transform 200ms ease',
                  '&:hover': {
                    backgroundColor: '#0aa8dc',
                    color: '#ffffff',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Icon sx={{ fontSize: 17 }} />
              </IconButton>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, sm: 3 },
            justifyContent: 'center'
          }}
        >
          {[
            { label: 'Secure', Icon: SecurityOutlinedIcon },
            { label: 'Reliable', Icon: GroupsOutlinedIcon },
            { label: 'Scalable', Icon: BarChartOutlinedIcon }
          ].map(({ label, Icon }) => (
            <Box key={label} sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <Icon sx={{ color: '#08a9df', fontSize: 24 }} />
              <Typography sx={{ color: '#d1dfeb', fontSize: 12 }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            alignItems: { xs: 'center', md: 'flex-end' },
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            textAlign: { xs: 'center', md: 'right' }
          }}
        >
          <Typography sx={{ color: '#a9c2d5', fontSize: 11 }}>
            {'\u00A9 2026 Retrack Automation. All rights reserved.'}
          </Typography>
          <Box sx={{
            alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.25, justifyContent: { xs: 'center', md: 'flex-end' }
          }}
          >
            {['Privacy Policy', 'Terms of Service', 'Support'].map((label, index) => (
              <Box key={label} component="span" sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
                {index > 0 && <Typography component="span" sx={{ color: 'rgba(185,207,224,0.45)', fontSize: 11 }}>|</Typography>}
                <Typography
                  component="span"
                  sx={{
                    color: '#b9cfe0',
                    cursor: 'default',
                    fontSize: 11,
                    '&:hover': { color: '#ffffff' }
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </LayoutWrapper>
  );
};

export default Login;
